import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { applyPreferencesPatch } from "@/lib/preferences";
import { UNIT_PREFERENCE_KEYS } from "@/lib/units";
import type { Session } from "@/types/session";
import type { UserPreferences, UserPreferencesPatch } from "@/types/users";

const SESSION_KEY = ["session"];

/** Whether a patch sets or clears any unit preference. */
function touchesUnits(patch: UserPreferencesPatch): boolean {
  return UNIT_PREFERENCE_KEYS.some(
    (key) => patch[key] !== undefined || (patch.clear ?? []).includes(key),
  );
}

/**
 * Updates the caller's server-side display preferences. Only the keys in the
 * patch change (and any in `clear` go back to unset).
 *
 * Optimistic: the cached session takes the change at once, so every reader of
 * `session.preferences` — the page's theme and chart classes, units, guidance,
 * the Maxwell rail — updates before the round trip. A failed save puts the
 * session back and says so; a successful one writes the server's full set.
 *
 * A unit change also refetches every other cached query: server-built tables
 * (the deals grid, exports) format their rates and volumes in the requesting
 * user's units, so anything fetched before the change holds the old ones.
 */
export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UserPreferencesPatch) =>
      api.patch<UserPreferences>("/api/me/preferences", patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: SESSION_KEY });
      const previous = qc.getQueryData<Session>(SESSION_KEY);
      if (previous) {
        qc.setQueryData<Session>(SESSION_KEY, {
          ...previous,
          preferences: applyPreferencesPatch(previous.preferences, patch),
        });
      }
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) qc.setQueryData(SESSION_KEY, context.previous);
      toast.error("Could not save your preference.");
    },
    onSuccess: (preferences, patch) => {
      qc.setQueryData<Session>(SESSION_KEY, (session) =>
        session ? { ...session, preferences } : session,
      );
      if (touchesUnits(patch)) {
        void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] });
      }
    },
  });
}
